import React, { useState } from 'react'
import Top from './Top'
import VFooter from './VFooter'
import { Card, Accordion } from './ui';
import { HelpCircle, ChevronDown, ChevronRight, User, Package, CreditCard, Shield, Settings, Phone, Search, Star } from 'lucide-react';

export default function Faqs() {
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const categories = [
    { id: 'all', name: 'All Questions', icon: <HelpCircle className="w-4 h-4" /> },
    { id: 'getting-started', name: 'Getting Started', icon: <User className="w-4 h-4" /> },
    { id: 'products', name: 'Products & Orders', icon: <Package className="w-4 h-4" /> },
    { id: 'payments', name: 'Payments & Fees', icon: <CreditCard className="w-4 h-4" /> },
    { id: 'account', name: 'Account & Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'technical', name: 'Technical Support', icon: <Settings className="w-4 h-4" /> }
  ];

  const faqs = [
    {
      category: 'getting-started',
      question: "What is Vasstr?",
      answer: "Vasstr is a comprehensive digital platform that connects fashion designers with customers. We provide tools for e-commerce, website building, payment processing, and business management. Designers can create their own online stores, showcase their collections, manage orders, and receive automated payments.",
      tags: ['platform', 'overview', 'marketplace']
    },
    {
      category: 'getting-started',
      question: "How do I register as a designer?",
      answer: "To register as a designer: 1) Click 'Sign Up' and choose 'Designer Account', 2) Complete the registration form with your basic information, 3) Complete KYC verification including PAN, bank details, and identity verification, 4) Once verified, you can access all designer features including product uploads and payment processing.",
      tags: ['registration', 'kyc', 'verification']
    },
    {
      category: 'getting-started',
      question: "What documents do I need for KYC verification?",
      answer: "For KYC verification, you need: Valid PAN card, Bank account details (account number, IFSC code), Identity proof (Aadhaar, passport, or driving license), Business registration documents (if applicable), and GST registration certificate (if applicable). All documents should be clear and readable.",
      tags: ['kyc', 'documents', 'verification', 'pan']
    },
    {
      category: 'products',
      question: "How many products can I upload?",
      answer: "There's no limit on the number of products you can upload. Each product can have up to 6 images, detailed descriptions, multiple fabric options, customization choices, and pricing tiers. You can also organize products into collections and categories.",
      tags: ['products', 'upload', 'limits', 'images']
    },
    {
      category: 'products',
      question: "Can customers customize my designs?",
      answer: "Yes! You can enable customization options for each product including fabric choices, color variations, size modifications, and design elements. Customers can request customizations, and you can review and approve changes before proceeding with the order.",
      tags: ['customization', 'fabric', 'colors', 'modifications']
    },
    {
      category: 'products',
      question: "How do I manage orders and track delivery status?",
      answer: "Your designer dashboard provides complete order management: View all incoming orders, update order status (talked to customer → started order → finished order → delivered), communicate with customers, track payment status, and generate invoices automatically.",
      tags: ['orders', 'status', 'delivery', 'dashboard']
    },
    {
      category: 'products',
      question: "What happens when a customer places an order?",
      answer: "When an order is placed: 1) You receive an email notification with order details, 2) Customer information and requirements appear in your dashboard, 3) You can contact the customer to discuss specifications, 4) Update order status as you progress, 5) Payment is processed automatically upon delivery confirmation.",
      tags: ['orders', 'notifications', 'process', 'workflow']
    },
    {
      category: 'payments',
      question: "How much commission does Vasstr charge?",
      answer: "Vasstr charges a 3% commission on all completed orders. This means you receive 97% of the order value. For example, on a ₹1,000 order, you receive ₹970, and ₹30 goes to platform fees. There are no upfront costs or monthly fees.",
      tags: ['commission', 'fees', 'pricing', 'percentage']
    },
    {
      category: 'payments',
      question: "When and how do I receive payments?",
      answer: "Payments are processed automatically: 1) Customer pays via Razorpay (cards, UPI, net banking), 2) Funds are held securely until delivery, 3) Once you mark order as 'delivered', payout is initiated, 4) You receive 97% of order value in your bank account within 24-48 hours. Minimum payout threshold is ₹500.",
      tags: ['payouts', 'timeline', 'bank transfer', 'threshold']
    },
    {
      category: 'payments',
      question: "What payment methods do customers have?",
      answer: "Customers can pay through Razorpay using: Credit/Debit cards (Visa, Mastercard, RuPay), UPI (PhonePe, Google Pay, Paytm), Net banking from 50+ banks, Digital wallets (Paytm, PhonePe, Amazon Pay), EMI options for high-value purchases, and Cash on Delivery (where available).",
      tags: ['payment methods', 'razorpay', 'upi', 'cards']
    },
    {
      category: 'payments',
      question: "How do refunds and cancellations work?",
      answer: "Refund policies depend on order status: Before production starts - full refund to customer, During production - case-by-case basis, After delivery - return/exchange policy applies. Platform commission is generally non-refundable. Disputes are handled through our mediation process.",
      tags: ['refunds', 'cancellations', 'policy', 'disputes']
    },
    {
      category: 'account',
      question: "How do I update my profile and bank details?",
      answer: "Access your profile settings from the dashboard: Personal information can be updated anytime, Bank details require re-verification for security, Business information updates may need document verification, Profile images and portfolio can be changed instantly. Critical changes may require admin approval.",
      tags: ['profile', 'bank details', 'updates', 'verification']
    },
    {
      category: 'account',
      question: "Is my design content secure and protected?",
      answer: "Yes, we implement multiple security measures: All images are stored with access controls, Bank details are encrypted with industry-standard encryption, Login sessions use JWT tokens with expiration, Regular security audits and updates, HTTPS encryption for all data transmission. You retain full ownership of your designs.",
      tags: ['security', 'encryption', 'design protection', 'ownership']
    },
    {
      category: 'account',
      question: "Can I create multiple designer stores or websites?",
      answer: "Yes! Our website builder allows you to: Create multiple template-based stores, Customize 20+ professional templates, Use your own domain (premium feature), Build separate stores for different product lines, Clone and modify existing templates, Export website data. Each store can have unique branding and products.",
      tags: ['websites', 'templates', 'stores', 'branding']
    },
    {
      category: 'technical',
      question: "How do I use the website builder and templates?",
      answer: "Our website builder is user-friendly: 1) Choose from 20+ professional templates, 2) Drag-and-drop customization interface, 3) Add your products, images, and content, 4) Customize colors, fonts, and layouts, 5) Preview on different devices, 6) Auto-save prevents data loss, 7) Publish when ready. No coding required!",
      tags: ['website builder', 'templates', 'customization', 'drag-drop']
    },
    {
      category: 'technical',
      question: "What image formats and sizes are supported?",
      answer: "Supported formats: JPEG, PNG, WebP (up to 10MB each). Recommended dimensions: Product images: 1000x1000px or higher, Banner images: 1920x800px, Gallery images: 800x800px, Profile pictures: 400x400px. The platform automatically optimizes images for web performance.",
      tags: ['images', 'formats', 'dimensions', 'optimization']
    },
    {
      category: 'technical',
      question: "Can I bulk upload products or import from other platforms?",
      answer: "Yes, we support bulk operations: CSV bulk upload for products with all details, Image batch upload (multiple files), Import from other e-commerce platforms (contact support), Template duplication for similar products, Bulk editing of prices and descriptions. Download our CSV template for easy bulk uploads.",
      tags: ['bulk upload', 'csv', 'import', 'batch processing']
    },
    {
      category: 'account',
      question: "What happens if I violate platform policies?",
      answer: "Policy violations are handled progressively: First violation - warning email and content removal, Repeat violations - temporary account suspension, Serious violations - permanent account termination, Appeal process available for disputed actions. Common violations include inappropriate content, copyright infringement, and fake products.",
      tags: ['policies', 'violations', 'suspension', 'termination']
    },
    {
      category: 'technical',
      question: "Do you provide customer support and how can I contact you?",
      answer: "We offer comprehensive support: Email support: team@vasstr.com, Phone: +91-40-1234-5678 (Mon-Sat, 9 AM - 7 PM), Live chat on platform (business hours), Help documentation and video tutorials, Community forum for peer support, Priority support for premium users.",
      tags: ['support', 'contact', 'help', 'documentation']
    },
    {
      category: 'getting-started',
      question: "Are there any fees to join as a designer?",
      answer: "No upfront fees! Vasstr is completely free to join: Free account creation and verification, Free website builder with templates, Free product listings (unlimited), Free order management tools, Free payment processing setup. You only pay the 3% commission on successful sales. No monthly fees, no setup charges.",
      tags: ['fees', 'free', 'commission only', 'no upfront cost']
    },
    {
      category: 'payments',
      question: "How do coupons and discounts work?",
      answer: "You have full control over discounts: Create percentage or fixed-amount coupons, Set minimum order requirements, Control usage limits and validity periods, Track coupon performance and analytics, Seasonal and promotional campaigns, Bulk discount management. Coupons are applied before commission calculation.",
      tags: ['coupons', 'discounts', 'promotions', 'analytics']
    },
    {
      category: 'technical',
      question: "Is there a mobile app for designers?",
      answer: "Currently, we offer a mobile-responsive web platform that works perfectly on all devices. Key features accessible on mobile: Order management and notifications, Customer communication, Basic product uploads, Sales analytics, Payment tracking. A dedicated mobile app is in development for 2026.",
      tags: ['mobile', 'app', 'responsive', 'development']
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      <Top/>
      <main className="pt-16">
        <section className="bg-gradient-to-b from-[#f4ece5] to-[#f8f3ed] py-16 px-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#ca7751] rounded-full mb-6">
                <HelpCircle className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Find answers to common questions about using Vasstr. Can't find what you're looking for? Contact our support team!
              </p>
            </div>

            {/* Search Bar */}
            <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search FAQs, questions, or keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ca7751] focus:border-[#ca7751] transition-colors"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 justify-center mb-8">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-200 ${
                    activeCategory === category.id
                      ? 'bg-[#ca7751] text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {category.icon}
                  <span className="font-medium">{category.name}</span>
                </button>
              ))}
            </div>

            {/* Results Count */}
            <div className="text-center mb-6">
              <p className="text-gray-600">
                Showing {filteredFaqs.length} question{filteredFaqs.length !== 1 ? 's' : ''}
                {searchTerm && ` for "${searchTerm}"`}
                {activeCategory !== 'all' && ` in ${categories.find(c => c.id === activeCategory)?.name}`}
              </p>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4">
              {filteredFaqs.length === 0 ? (
                <div className="bg-white p-12 rounded-xl shadow-lg text-center">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No results found</h3>
                  <p className="text-gray-600 mb-6">
                    Try adjusting your search terms or browse different categories.
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setActiveCategory('all');
                    }}
                    className="bg-[#ca7751] text-white px-6 py-2 rounded-lg hover:bg-[#b86a45] transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                filteredFaqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                    <button
                      className={`w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 transition-colors duration-200 ${
                        activeAccordion === index ? 'bg-gray-50' : ''
                      }`}
                      onClick={() => toggleAccordion(index)}
                    >
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                        <div className="flex flex-wrap gap-2">
                          {faq.tags.slice(0, 3).map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="text-xs px-2 py-1 bg-[#f4ece5] text-[#ca7751] rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="ml-4">
                        {activeAccordion === index ? (
                          <ChevronDown className="w-6 h-6 text-[#ca7751]" />
                        ) : (
                          <ChevronRight className="w-6 h-6 text-[#ca7751]" />
                        )}
                      </div>
                    </button>

                    {activeAccordion === index && (
                      <div className="px-6 pb-6">
                        <div className="border-l-4 border-[#ca7751] pl-4">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Contact Support Section */}
            <div className="bg-gradient-to-r from-[#ca7751] to-[#d67e5a] p-8 rounded-xl shadow-lg mt-12 text-white text-center">
              <Star className="w-12 h-12 mx-auto mb-4 opacity-80" />
              <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
              <p className="text-white/90 mb-6 max-w-2xl mx-auto">
                Our support team is here to help! Get personalized assistance with your account, technical issues, or any other questions.
              </p>
              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <div className="flex items-center space-x-3 justify-center md:justify-start">
                  <Phone className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-semibold">Call Us</p>
                    <p className="text-white/90">+91 9704387878</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 justify-center md:justify-end">
                  <HelpCircle className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-semibold">Email Support</p>
                    <p className="text-white/90">team@Vasstr.com</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-white/80 mt-4">
                Response time: Within 24 hours | Phone support: Mon-Sat, 9 AM - 7 PM IST
              </p>
            </div>
          </div>
        </section>
      </main>
      <VFooter/>
    </div>
  )
}
